import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { buildApiUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import IncomeSourceModal from './components/IncomeSourceModal';
import IncomeSourceTable from './components/IncomeSourceTable';
import IncomeSummaryCards from './components/IncomeSummaryCards';
import IncomeProjectionChart from './components/IncomeProjectionChart';
import IncomeTabNavigation from './components/IncomeTabNavigation';
import SurplusRoutingSection from './components/SurplusRoutingSection';
import { useUserData } from '../../hooks/useUserData';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useTour } from '../../contexts/TourContext';
import { useDemo } from '../../contexts/DemoContext';
import VideoModal from '../../components/ui/VideoModal';
import { getVideoForPage } from '../../config/videoConfig';
import DemoModeBanner from '../../components/demo/DemoModeBanner';

const IncomeManagement = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  
  // Use the schema-based user data hook
  const { 
    userData, 
    loading, 
    error, 
    addIncomeSource, 
    updateIncomeSource, 
    deleteIncomeSource 
  } = useUserData();

  // Get income sources and projection horizon from user data
  const incomeSources = userData?.income || [];
  const projectionHorizon = userData?.setup?.projectionHorizon || 20;
  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams, isDemoModeActive } = useDemo();
  const [diversification, setDiversification] = useState(null);
  const { canWrite } = useSubscription();
  const effectiveCanWrite = canWrite && !isDemoModeActive;
  const { startIncomeManagementTour, autoStartTour } = useTour();
  const videoConfig = getVideoForPage('income-management');

  useEffect(() => {
    const fetchDiversification = async () => {
      try {
        const queryParams = getDemoQueryParams();
        const res = await fetch(buildApiUrl(`/api/income/diversification${queryParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load diversification');
        const json = await res.json();
        if (json?.success) setDiversification(json.data);
      } catch (e) {
        console.error(e);
        // fallback stays null and UI will render client calc (commented in code below)
      }
    };
    fetchDiversification();
  }, [userData, getDemoQueryParams, getAuthHeaders]);

  // Auto-start tour for first-time visitors
  useEffect(() => {
    if (!loading && userData) {
      autoStartTour('income-management', startIncomeManagementTour);
    }
  }, [loading, userData, autoStartTour, startIncomeManagementTour]);

  const handleSaveIncomeSource = async (sourceData) => {
    try {
      if (editingSource) {
        // Update existing income source
        const result = await updateIncomeSource(sourceData);
        if (result.success) {
          setIsModalOpen(false);
          setEditingSource(null);
        } else {
          console.error('Failed to update income source:', result.error);
        }
      } else {
        // Add new income source
        const result = await addIncomeSource(sourceData);
        if (result.success) {
          setIsModalOpen(false);
        } else {
          console.error('Failed to add income source:', result.error);
        }
      }
    } catch (error) {
      console.error('Error saving income source:', error);
    }
  };

  const handleEditIncomeSource = (source) => {
    setEditingSource(source);
    setIsModalOpen(true);
  };

  const handleDeleteIncomeSource = async (sourceId) => {
    try {
      const result = await deleteIncomeSource(sourceId);
      if (!result.success) {
        console.error('Failed to delete income source:', result.error);
      }
    } catch (error) {
      console.error('Error deleting income source:', error);
    }
  };

  const handleAddIncomeSource = () => {
    setEditingSource(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSource(null);
  };

  // Filter income sources based on active tab
  const filteredIncomeSources = activeTab === 'all' 
    ? incomeSources 
    : incomeSources.filter(source => source.type === activeTab);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading income data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Data</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isExpanded={sidebarExpanded} 
        onToggle={() => setSidebarExpanded(!sidebarExpanded)} 
      />
      
      <Header 
        sidebarExpanded={sidebarExpanded}
        onSidebarToggle={() => setSidebarExpanded(!sidebarExpanded)}
      />

      <DemoModeBanner />

      <main 
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-8
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="p-4 sm:p-6 py-6 sm:py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8" data-tour="income-header">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Income Management</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                {`Configure and project all income streams with growth modeling over your ${projectionHorizon}-year planning horizon`}
              </p>
            </div>
            
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={() => setIsVideoOpen(true)}
                iconName="Video"
                iconPosition="left"
                className="hidden sm:inline-flex"
              >
                Learn
              </Button>
              <Button
                variant="outline"
                onClick={startIncomeManagementTour}
                iconName="HelpCircle"
                iconPosition="left"
                className="hidden sm:inline-flex"
              >
                Tour
              </Button>
              <Button
                variant="default"
                onClick={handleAddIncomeSource}
                iconName="Plus"
                iconPosition="left"
                className="disabled:opacity-60"
                disabled={!effectiveCanWrite}
              >
                <span className="hidden sm:inline">Add Income Source</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div data-tour="income-summary">
            <IncomeSummaryCards 
              incomeSources={incomeSources}
              activeTab={activeTab}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:gap-8">
            {/* Income Sources */}
            <div className="space-y-6">
              {/* Tab Navigation */}
              <IncomeTabNavigation
                activeTab={activeTab}
                onTabChange={setActiveTab}
                incomeSources={incomeSources}
              />

              {/* Income Sources Table */}
              <div data-tour="income-table">
                <IncomeSourceTable
                  incomeSources={incomeSources}
                  activeTab={activeTab}
                  onEdit={handleEditIncomeSource}
                  onDelete={handleDeleteIncomeSource}
                />
              </div>
            </div>
          </div>

          {/* Additional Insights Section */}
          <div className="mt-8 sm:mt-12 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {/* Surplus Routing inside Income Management */}
            <div className="lg:col-span-2" data-tour="income-surplus">
              <SurplusRoutingSection />
            </div>
            {/* Income Projections */}
            <div className="lg:col-span-2" data-tour="income-chart">
              <IncomeProjectionChart incomeSources={incomeSources} projectionHorizon={projectionHorizon} />
            </div>
            {/* Income Diversification */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="PieChart" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Income Diversification</h3>
                  <p className="text-sm text-muted-foreground">Risk assessment across income types</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {['salary', 'business', 'rental', 'passive'].map(type => {
                  const percentage = (() => {
                    if (diversification?.byType) {
                      const item = diversification.byType.find((b) => b.type === type);
                      return item ? item.percentage : 0;
                    }
                    // Frontend fallback (kept for reference)
                    // const typeTotal = incomeSources
                    //   .filter(s => s.type === type)
                    //   .reduce((sum, s) => sum + (s.currentAmount * (s.frequency === 'monthly' ? 12 : s.frequency === 'quarterly' ? 4 : 1)), 0);
                    // const totalIncome = incomeSources.reduce((sum, s) => sum + (s.currentAmount * (s.frequency === 'monthly' ? 12 : s.frequency === 'quarterly' ? 4 : 1)), 0);
                    // return totalIncome > 0 ? (typeTotal / totalIncome) * 100 : 0;
                    return 0;
                  })();

                  return (
                    <div key={type} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground capitalize">{type}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              type === 'salary' ? 'bg-primary' :
                              type === 'business' ? 'bg-accent' :
                              type === 'rental' ? 'bg-warning' : 'bg-secondary'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-12 text-right">
                          {percentage.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Planning Insights - temporarily disabled
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Lightbulb" size={20} className="text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Planning Insights</h3>
                  <p className="text-sm text-muted-foreground">Personalized recommendations</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-success/5 rounded-lg border border-success/20">
                  <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Strong Income Growth</p>
                    <p className="text-xs text-muted-foreground">Your average growth rate of 6.1% exceeds inflation targets</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-warning/5 rounded-lg border border-warning/20">
                  <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Consider Diversification</p>
                    <p className="text-xs text-muted-foreground">65% of income from salary - consider additional passive streams</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <Icon name="TrendingUp" size={16} className="text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Retirement Readiness</p>
                    <p className="text-xs text-muted-foreground">On track to meet 80% income replacement by age 65</p>
                  </div>
                </div>
              </div>
            </div>
            */}
          </div>
        </div>
      </main>

      {/* Income Source Modal */}
      <IncomeSourceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveIncomeSource}
        editingSource={editingSource}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl={videoConfig?.url}
        title={videoConfig?.title}
      />
    </div>
  );
};

export default IncomeManagement;